import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import {
  Box,
  Typography,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  LinearProgress,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Timer as TimerIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Send as SubmitIcon,
  Replay as RetryIcon,
  Home as HomeIcon,
  EmojiEvents as TrophyIcon,
  RadioButtonUnchecked as UncheckedIcon,
  CheckCircle as CheckedIcon,
} from '@mui/icons-material';
import {
  quizApi,
  Quiz,
  QuizQuestion,
  QuizAttempt,
  QuizSubmitResult,
} from '../services/api';

export default function QuizPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Quiz state
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [result, setResult] = useState<QuizSubmitResult | null>(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  // Fetch quiz
  const { data: quiz, isLoading, error } = useQuery<Quiz>({
    queryKey: ['quiz', quizId],
    queryFn: () => quizApi.getQuiz(quizId!).then((r) => r.data),
    enabled: !!quizId,
  });

  // Start attempt mutation
  const startMutation = useMutation({
    mutationFn: () => quizApi.startAttempt(quizId!),
    onSuccess: (response) => {
      setAttempt(response.data);
      if (quiz?.timeLimitMinutes) {
        setTimeRemaining(quiz.timeLimitMinutes * 60);
      }
    },
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: () => quizApi.submitQuiz(quizId!, attempt!.id, answers),
    onSuccess: (response) => {
      setResult(response.data);
    },
  });

  // Timer effect
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0 || result) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          // Auto-submit when time runs out
          if (attempt && !result) {
            submitMutation.mutate();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, result, attempt]);

  // Handle answer change for single-choice and true-false
  const handleSingleAnswer = (questionId: string, answerId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: [answerId] }));
  };

  // Handle answer change for multiple-choice
  const handleMultipleAnswer = (questionId: string, answerId: string, checked: boolean) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, answerId] };
      }
      return { ...prev, [questionId]: current.filter((id) => id !== answerId) };
    });
  };

  // Handle free-text answer
  const handleFreeTextAnswer = (questionId: string, text: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: [text] }));
  };

  // Format time remaining
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Swipe handlers for mobile question navigation
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (quiz && currentQuestionIndex < quiz.questions.length - 1 && isMobile) {
        setCurrentQuestionIndex((prev) => prev + 1);
      }
    },
    onSwipedRight: () => {
      if (currentQuestionIndex > 0 && isMobile) {
        setCurrentQuestionIndex((prev) => prev - 1);
      }
    },
    preventScrollOnSwipe: false,
    trackMouse: false,
    delta: 50,
  });

  // Get course info for navigation
  const courseSlug = quiz?.module?.course?.slug || quiz?.course?.slug;

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !quiz) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('errors.generic')}
      </Alert>
    );
  }

  // Result view
  if (result) {
    return (
      <Box maxWidth="md" mx="auto" p={2}>
        <Card sx={{ textAlign: 'center', p: 3 }}>
          <CardContent>
            {result.passed ? (
              <>
                <TrophyIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" color="success.main" gutterBottom>
                  {t('quiz.passed', 'Bestanden!')}
                </Typography>
              </>
            ) : (
              <>
                <CloseIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" color="error.main" gutterBottom>
                  {t('quiz.failed', 'Nicht bestanden')}
                </Typography>
              </>
            )}

            <Typography variant="h2" sx={{ my: 3 }}>
              {result.scorePercent}%
            </Typography>

            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" mb={3}>
              <Chip
                label={`${t('quiz.passingScore', 'Bestehensgrenze')}: ${result.passingScore}%`}
                variant="outlined"
              />
              {result.pointsEarned > 0 && (
                <Chip
                  label={`+${result.pointsEarned} ${t('common.points', 'Punkte')}`}
                  color="success"
                />
              )}
            </Box>

            {/* Question results */}
            <Box textAlign="left" mt={4}>
              <Typography variant="h6" gutterBottom>
                {t('quiz.questionResults', 'Ergebnisse')}
              </Typography>
              {result.results.map((qResult, index) => {
                const question = quiz.questions.find((q) => q.id === qResult.questionId);
                return (
                  <Paper
                    key={qResult.questionId}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: 4,
                      borderColor: qResult.correct ? 'success.main' : 'error.main',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {qResult.correct ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                      <Typography variant="subtitle1">
                        {t('quiz.question', 'Frage')} {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="body1" mb={1}>
                      {question?.questionText}
                    </Typography>
                    {qResult.explanation && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {qResult.explanation}
                      </Alert>
                    )}
                  </Paper>
                );
              })}
            </Box>

            <Box display="flex" justifyContent="center" gap={2} mt={4}>
              {!result.passed && quiz.maxAttempts && (
                <Button
                  variant="outlined"
                  startIcon={<RetryIcon />}
                  onClick={() => {
                    setResult(null);
                    setAttempt(null);
                    setAnswers({});
                    setCurrentQuestionIndex(0);
                  }}
                >
                  {t('quiz.tryAgain', 'Erneut versuchen')}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={() => navigate(courseSlug ? `/courses/${courseSlug}` : '/courses')}
              >
                {t('quiz.backToCourse', 'Zurück zum Kurs')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Start screen
  if (!attempt) {
    return (
      <Box maxWidth="md" mx="auto" p={2}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h4" gutterBottom>
              {quiz.title}
            </Typography>
            {quiz.description && (
              <Typography variant="body1" color="text.secondary" mb={3}>
                {quiz.description}
              </Typography>
            )}

            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" mb={4}>
              <Chip
                icon={<CheckIcon />}
                label={`${quiz.questions.length} ${t('quiz.questions', 'Fragen')}`}
              />
              <Chip
                label={`${t('quiz.passingScore', 'Bestehensgrenze')}: ${quiz.passingScorePercent}%`}
              />
              {quiz.timeLimitMinutes && (
                <Chip
                  icon={<TimerIcon />}
                  label={`${quiz.timeLimitMinutes} ${t('quiz.minutes', 'Minuten')}`}
                />
              )}
              {quiz.maxAttempts && (
                <Chip label={`${t('quiz.maxAttempts', 'Max. Versuche')}: ${quiz.maxAttempts}`} />
              )}
              <Chip
                color="success"
                label={`+${quiz.pointsReward} ${t('common.points', 'Punkte')}`}
              />
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={() => startMutation.mutate()}
              disabled={startMutation.isPending}
            >
              {startMutation.isPending
                ? t('common.loading', 'Laden...')
                : t('quiz.start', 'Quiz starten')}
            </Button>

            {startMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t('quiz.maxAttemptsReached', 'Maximale Anzahl der Versuche erreicht')}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Quiz view
  const currentQuestion = quiz.questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / quiz.questions.length) * 100;

  return (
    <Box
      {...(isMobile ? swipeHandlers : {})}
      sx={{
        maxWidth: 'md',
        mx: 'auto',
        p: { xs: 1, sm: 2 },
        pb: { xs: 10, sm: 2 }, // Extra padding for fixed bottom nav on mobile
        touchAction: isMobile ? 'pan-y' : 'auto',
      }}
    >
      {/* Header with timer and progress - sticky on mobile */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          position: isMobile ? 'sticky' : 'relative',
          top: isMobile ? 0 : 'auto',
          zIndex: isMobile ? 10 : 'auto',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} noWrap sx={{ flex: 1, mr: 1 }}>
            {quiz.title}
          </Typography>
          {timeRemaining !== null && (
            <Chip
              icon={<TimerIcon />}
              label={formatTime(timeRemaining)}
              color={timeRemaining < 60 ? 'error' : timeRemaining < 300 ? 'warning' : 'default'}
              size={isMobile ? 'medium' : 'medium'}
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flexGrow: 1, height: { xs: 6, sm: 8 }, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {answeredCount}/{quiz.questions.length}
          </Typography>
        </Box>
      </Paper>

      {/* Question stepper - simplified on mobile */}
      {isMobile ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          {quiz.questions.map((q, index) => (
            <Box
              key={q.id}
              onClick={() => setCurrentQuestionIndex(index)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor:
                  index === currentQuestionIndex
                    ? 'primary.main'
                    : answers[q.id]?.length
                      ? 'success.light'
                      : 'grey.300',
                color:
                  index === currentQuestionIndex
                    ? 'primary.contrastText'
                    : answers[q.id]?.length
                      ? 'success.contrastText'
                      : 'text.secondary',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                transition: 'all 0.2s',
                '&:active': { transform: 'scale(0.95)' },
              }}
            >
              {index + 1}
            </Box>
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 2, mb: 3, overflowX: 'auto' }}>
          <Stepper nonLinear activeStep={currentQuestionIndex}>
            {quiz.questions.map((q, index) => (
              <Step key={q.id} completed={!!answers[q.id]?.length}>
                <StepButton onClick={() => setCurrentQuestionIndex(index)}>{index + 1}</StepButton>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      {/* Current question */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('quiz.question', 'Frage')} {currentQuestionIndex + 1} {t('common.of', 'von')}{' '}
              {quiz.questions.length}
            </Typography>
            <Chip
              size="small"
              label={`${currentQuestion.points} ${currentQuestion.points === 1 ? t('quiz.point', 'Punkt') : t('common.points', 'Punkte')}`}
            />
          </Box>

          <Typography variant="h6" mb={3}>
            {currentQuestion.questionText}
          </Typography>

          {/* Answer options based on question type - touch-optimized cards on mobile */}
          {(currentQuestion.questionType === 'single-choice' ||
            currentQuestion.questionType === 'true-false') && (
            isMobile ? (
              // Mobile: Full-width touch cards
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {currentQuestion.answers.map((answer) => {
                  const isSelected = answers[currentQuestion.id]?.[0] === answer.id;
                  return (
                    <Paper
                      key={answer.id}
                      onClick={() => handleSingleAnswer(currentQuestion.id, answer.id)}
                      sx={{
                        p: 2,
                        minHeight: 56,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: 'pointer',
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:active': { transform: 'scale(0.98)' },
                      }}
                    >
                      {isSelected ? (
                        <CheckedIcon color="primary" sx={{ fontSize: 28 }} />
                      ) : (
                        <UncheckedIcon sx={{ fontSize: 28, color: 'grey.400' }} />
                      )}
                      <Typography variant="body1">{answer.answerText}</Typography>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              // Desktop: Traditional radio group
              <RadioGroup
                value={answers[currentQuestion.id]?.[0] || ''}
                onChange={(e) => handleSingleAnswer(currentQuestion.id, e.target.value)}
              >
                {currentQuestion.answers.map((answer) => (
                  <FormControlLabel
                    key={answer.id}
                    value={answer.id}
                    control={<Radio />}
                    label={answer.answerText}
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
              </RadioGroup>
            )
          )}

          {currentQuestion.questionType === 'multiple-choice' && (
            isMobile ? (
              // Mobile: Full-width touch cards for multiple choice
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {currentQuestion.answers.map((answer) => {
                  const isSelected = answers[currentQuestion.id]?.includes(answer.id) || false;
                  return (
                    <Paper
                      key={answer.id}
                      onClick={() => handleMultipleAnswer(currentQuestion.id, answer.id, !isSelected)}
                      sx={{
                        p: 2,
                        minHeight: 56,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: 'pointer',
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:active': { transform: 'scale(0.98)' },
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          handleMultipleAnswer(currentQuestion.id, answer.id, e.target.checked)
                        }
                      />
                      <Typography variant="body1">{answer.answerText}</Typography>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              // Desktop: Traditional checkbox group
              <FormGroup>
                {currentQuestion.answers.map((answer) => (
                  <FormControlLabel
                    key={answer.id}
                    control={
                      <Checkbox
                        checked={answers[currentQuestion.id]?.includes(answer.id) || false}
                        onChange={(e) =>
                          handleMultipleAnswer(currentQuestion.id, answer.id, e.target.checked)
                        }
                      />
                    }
                    label={answer.answerText}
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
              </FormGroup>
            )
          )}

          {currentQuestion.questionType === 'free-text' && (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={answers[currentQuestion.id]?.[0] || ''}
              onChange={(e) => handleFreeTextAnswer(currentQuestion.id, e.target.value)}
              placeholder={t('quiz.enterAnswer', 'Ihre Antwort eingeben...')}
            />
          )}
        </CardContent>
      </Card>

      {/* Navigation buttons - fixed at bottom on mobile */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          position: isMobile ? 'fixed' : 'relative',
          bottom: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          p: isMobile ? 2 : 0,
          mt: isMobile ? 0 : 2,
          bgcolor: isMobile ? 'background.paper' : 'transparent',
          borderTop: isMobile ? 1 : 0,
          borderColor: 'divider',
          zIndex: isMobile ? 100 : 'auto',
          boxShadow: isMobile ? '0 -2px 10px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        <Button
          startIcon={<PrevIcon />}
          onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
          disabled={currentQuestionIndex === 0}
          variant={isMobile ? 'outlined' : 'text'}
          sx={{
            minHeight: { xs: 48, sm: 'auto' },
            minWidth: { xs: 48, sm: 'auto' },
            px: { xs: 2, sm: 2 },
          }}
        >
          {isMobile ? '' : t('common.previous', 'Zurück')}
        </Button>

        {/* Question indicator on mobile */}
        {isMobile && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
            {currentQuestionIndex + 1} / {quiz.questions.length}
          </Typography>
        )}

        <Box display="flex" gap={1}>
          {currentQuestionIndex < quiz.questions.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
              sx={{
                minHeight: { xs: 48, sm: 'auto' },
                px: { xs: 3, sm: 2 },
              }}
            >
              {t('common.next', 'Weiter')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              endIcon={<SubmitIcon />}
              onClick={() => setConfirmSubmit(true)}
              disabled={submitMutation.isPending}
              sx={{
                minHeight: { xs: 48, sm: 'auto' },
                px: { xs: 3, sm: 2 },
              }}
            >
              {t('quiz.submit', 'Abgeben')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Confirm submit dialog - fullScreen on mobile */}
      <Dialog
        open={confirmSubmit}
        onClose={() => setConfirmSubmit(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('quiz.confirmSubmit', 'Quiz abgeben?')}</DialogTitle>
        <DialogContent>
          <Typography>
            {answeredCount < quiz.questions.length
              ? t(
                  'quiz.unansweredQuestions',
                  `Sie haben ${quiz.questions.length - answeredCount} Fragen noch nicht beantwortet. Möchten Sie trotzdem abgeben?`
                )
              : t('quiz.confirmSubmitText', 'Möchten Sie das Quiz jetzt abgeben?')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button
            onClick={() => setConfirmSubmit(false)}
            sx={{ minHeight: { xs: 48, sm: 'auto' } }}
          >
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmSubmit(false);
              submitMutation.mutate();
            }}
            disabled={submitMutation.isPending}
            sx={{ minHeight: { xs: 48, sm: 'auto' } }}
          >
            {submitMutation.isPending
              ? t('common.loading', 'Laden...')
              : t('quiz.submit', 'Abgeben')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
